/**
 * Represents the result of parsing a Google Font input
 */
export interface GoogleFontResult {
	fontUri: string;
	fontName: string;
}

/**
 * Parses Google Font input and extracts the font URL and font family name.
 * Handles both direct URLs and full HTML link tags.
 *
 * @param input - The input string, which can be:
 *   - A direct Google Fonts URL: https://fonts.googleapis.com/css2?family=Josefin+Sans...
 *   - An HTML link tag: <link href="https://fonts.googleapis.com/..." rel="stylesheet">
 * @returns An object containing the extracted fontUri and fontName
 *
 * @example
 * // Direct URL
 * parseGoogleFontInput('https://fonts.googleapis.com/css2?family=Roboto')
 * // Returns: { fontUri: 'https://fonts.googleapis.com/css2?family=Roboto', fontName: 'Roboto' }
 *
 * @example
 * // HTML link tag
 * parseGoogleFontInput('<link href="https://fonts.googleapis.com/css2?family=Open+Sans" rel="stylesheet">')
 * // Returns: { fontUri: 'https://fonts.googleapis.com/css2?family=Open+Sans', fontName: 'Open Sans' }
 */
export function parseGoogleFontInput(input: string): GoogleFontResult {
	let fontUri = input.trim();
	let fontName = '';

	// Extract URL from <link> tag if user pasted HTML
	// Handle formats like: <link href="https://fonts.googleapis.com/..." rel="stylesheet">
	if (fontUri.includes('<link') && fontUri.includes('href=')) {
		const hrefMatch = fontUri.match(/href=["']([^"']+)["']/);
		if (hrefMatch && hrefMatch[1]) {
			fontUri = hrefMatch[1];
		}
	}

	// Extract font family name from URL if present
	// Format: https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@...
	if (fontUri.includes('fonts.googleapis.com') && fontUri.includes('family=')) {
		const familyMatch = fontUri.match(/family=([^:&>]+)/);
		if (familyMatch && familyMatch[1]) {
			// Convert URL encoding (+ or %20) back to spaces
			fontName = decodeURIComponent(familyMatch[1].replace(/\+/g, ' '));
		}
	}

	return { fontUri, fontName };
}
